// PlanCard — one funnel-framed plan card, rendered to match the approved
// artifact (billing-page-mockup.html) exactly.
//
// PRESENTATION ONLY. Every number (price, billing note, platform fee) is fed in
// as an already-engine-derived string by BillingPlansPage; this card hard-codes
// no rates or prices. The CTA encodes the checkout-eligibility boundary via the
// `ctaKind` the parent computes:
//   - current   → "✓ Your current plan" (disabled)
//   - upgrade   → "Upgrade to X"   (fires checkout)
//   - downgrade → "Downgrade to X" (fires checkout — paid→paid)
//   - unavailable → disabled (e.g. downgrade-to-free has no self-serve path)
//   - resume    → "Resume plan" (enabled; the recovery action while a
//                 cancellation is pending — actionable but not an upgrade)

import { Fragment, useId, useState } from 'react';
import type { Plan } from '@charitystack/entitlements';
import { Badge } from '@/components/ui/badge';
import type {
  FeeMode,
  PlanFeatureItem,
  PlanPresentation,
} from '../../../../utils/billingPlans';

// Below the `xl` breakpoint (viewport < 1280px), the feature list collapses to
// this many headline items with a "See all features" expander revealing the
// rest; at xl+ the full grouped list always renders (see billing-plans-page.css
// `@media` rules). The cap keeps the tallest card (Base) short on the narrow
// screens where vertical space is tight, without losing the funnel detail.
const COLLAPSED_FEATURE_CAP = 4;

// Matches the dashboard's canonical StatusBadge styling (StatusBadge.jsx) so the
// plan pills read as the same badge system as transaction/contact status pills:
// rounded-md, text-sm, font-medium, horizontal padding, centered content. Omits
// StatusBadge's `min-w-[100px]` — that exists to line up table cells, which a
// label-on-a-border pill must not be forced to. Exported so the banner status
// pill (BillingPlansPage) uses the exact same class string.
export const BADGE_STATUS_CLASS = 'rounded-md text-sm font-medium px-3 justify-center';

// `resume` (PE-1993) is ACTIONABLE but deliberately NOT `upgrade`: during a
// pending cancellation the current-plan card's action is "Resume plan", and
// classifying it as an upgrade would leak an upgrade proration note and trial
// copy onto the card (both keyed on kind === 'upgrade' in BillingPlansPage).
export type PlanCtaKind = 'current' | 'upgrade' | 'downgrade' | 'unavailable' | 'resume';
// `trial`, when set, is the emphasized "N-day free trial, then …" headline shown
// in place of the muted price-context note (highest-conversion treatment: the
// trial leads, the big price above stays prominent). Engine-derived upstream.
export type BillingNote = {
  text: string;
  save?: string | null;
  trial?: string | null;
  // Optional qualifier appended INLINE to the "/mo" price unit (e.g.
  // ", billed annually"), so an annual cycle reads "$29 /mo, billed annually"
  // — the cadence can't be missed at the price. Set only by the checkout context
  // panel; the funnel grid leaves it undefined and renders the bare "/mo".
  unitSuffix?: string | null;
};

type PlanCardProps = {
  plan: Plan;
  presentation: PlanPresentation;
  priceLabel: string | null; // "$29" | "$0" | "Custom"
  showUnit: boolean; // render the "/mo" unit (paid tiers)
  billingNote: BillingNote; // sub-price line; optional amber "save $X" span
  feeMode: FeeMode;
  feeCovered: string | null; // engine-derived covered rate ("6%")
  feeUncovered: string | null; // engine-derived uncovered rate ("4%")
  isCurrent: boolean;
  isRecommended: boolean; // funnel "Most popular" (Plus)
  ctaKind: PlanCtaKind;
  ctaLabel: string;
  ctaTitle?: string;
  onUpgrade: () => void; // only wired for upgrade/downgrade
  pending: boolean;
  // COMPACT (PE-1572): render the card as a context panel inside the Embedded
  // Checkout modal's left column — same price/fee/feature content, but WITHOUT
  // the funnel chrome that makes no sense at the point of purchase: no CTA button
  // (the user already clicked upgrade; the live Stripe form is the action), no
  // "Most popular" badge, and no see-all expander (the full grouped list shows).
  // Purely additive; omitted/false → the full funnel card renders unchanged.
  compact?: boolean;
  // Does the VIEWING org already have this entitlement feature? Used to hide a
  // feature group on an UPGRADE card that the org already holds via a
  // grandfather override (e.g. legacy_starter already has growth tools +
  // contacts, so the Base card shows only the genuinely-new Marketing group).
  // Returns false for a stock org on every higher-tier key, so the full card
  // renders unless there's a real override exception. Omitted → no hiding.
  alreadyHasFeature?: (featureKey: string) => boolean;
};

// Hero-line marker — the bare Heroicons `check` in charitystack sage (replaces
// the old decorative star). Matches the feature-row Check below.
function HeroCheck() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6B8E6B"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// Disclosure chevron for the compact-mode group dropdowns. Same path/stroke as
// the style guide's section-header-bar chevron (style-guide.html → "Collapsible
// Steps"); rotates 180° via CSS when its parent <details> is open.
function Chevron() {
  return (
    <svg
      className="pf-acc-chev"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

// Feature-row bullet — the bare Heroicons `check` in charitystack sage (no
// circle disc). Lighter and more precise than the old check-circle.
function Check() {
  return (
    <svg
      className="pf-check"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6B8E6B"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// One feature line (+ its optional "limit" sub-line). Shared by the collapsed
// top list and the expanded grouped detail so they render identically.
function FeatureRow({ item }: { item: PlanFeatureItem }) {
  return (
    <Fragment>
      <li className="pf-item">
        <Check />
        <span className={item.hl ? 'hl' : undefined}>
          {item.text}
          {item.was ? <span className="pf-was"> ({item.was})</span> : null}
        </span>
      </li>
      {item.limit ? <li className="pf-limit">{item.limit}</li> : null}
    </Fragment>
  );
}

export function PlanCard({
  presentation,
  priceLabel,
  showUnit,
  billingNote,
  feeMode,
  feeCovered,
  feeUncovered,
  isCurrent,
  isRecommended,
  ctaKind,
  ctaLabel,
  ctaTitle,
  onUpgrade,
  pending,
  alreadyHasFeature,
  compact = false,
}: PlanCardProps) {
  // Hide feature groups the viewing org already holds — but ONLY on an UPGRADE
  // card, and only when this org actually has the group's representative feature
  // (true for a grandfathered override like legacy_starter, false for a stock
  // org). NEVER filter the current-plan card (shows its full set) NOR a DOWNGRADE
  // card: a PLUS org viewing the lower BASE card (ctaKind 'downgrade') `can()`
  // the growth-tools/contacts keys, so without the upgrade-only guard the
  // downgrade card would be truncated to just Marketing — misleading. Groups
  // without a featureKey (e.g. Marketing — the net-new unlock) always render.
  // This turns the Base card into "just Marketing" for a grandfathered
  // legacy_starter org while a stock org (and any downgrade viewer) sees all.
  const visibleGroups =
    ctaKind !== 'upgrade' || !alreadyHasFeature
      ? presentation.groups
      : presentation.groups.filter(
          (g) => !(g.featureKey && alreadyHasFeature(g.featureKey)),
        );

  // Below xl (viewport < 1280px) the feature list collapses to the first
  // COLLAPSED_FEATURE_CAP items with a "See all" expander; at xl+ the full
  // grouped list always renders (CSS decides which is visible). The flat
  // top/rest split is computed once from the (override-filtered) groups — purely
  // a presentation slice.
  const flatItems = visibleGroups.flatMap((g) => g.items);
  const topItems = flatItems.slice(0, COLLAPSED_FEATURE_CAP);
  const hasExtra = flatItems.length > COLLAPSED_FEATURE_CAP;

  // Per-card expander state (only meaningful below xl; at xl+ the See-all
  // button is hidden and the full list shows regardless).
  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const extraId = useId();

  let cardClass = 'plan-card';
  if (isCurrent) cardClass += ' current';
  // Current takes full visual precedence over recommended: when this is the org's
  // current plan, suppress the sage "recommended" border so the neutral
  // current-plan treatment isn't overridden by the popular highlight.
  if (isRecommended && !isCurrent) cardClass += ' recommended';
  // In compact (checkout) mode the full grouped list always shows — there is no
  // expander, so force the features-open state regardless of the xl breakpoint.
  if (featuresExpanded || compact) cardClass += ' features-open';
  if (compact) cardClass += ' plan-card--compact';

  // Card pill — the shared <Badge> matched to the dashboard's StatusBadge look
  // (rounded-md, text-sm, an inner inline-flex span). `BADGE_STATUS_CLASS` is the
  // same class string StatusBadge applies, so plan pills read as the same badge
  // system as transaction/contact status pills. `.plan-card-badge` adds ONLY the
  // centered-on-border positioning (no shape override). `success` = sage (Most
  // popular); `neutral` = muted (Current plan). Current takes FULL precedence: when
  // a plan is both the org's current plan AND the recommended tier, only "Current
  // plan" shows — the "Most popular" badge is suppressed entirely (not merged into
  // a "Current · Most popular" label), matching the suppressed sage border above.
  let badge: { variant: 'success' | 'neutral'; text: string } | null = null;
  if (isCurrent) badge = { variant: 'neutral', text: 'Current plan' };
  else if (isRecommended) badge = { variant: 'success', text: 'Most popular' };

  const isCheckoutCta = ctaKind === 'upgrade' || ctaKind === 'downgrade';
  // `resume` is clickable too, but it is not a checkout — it reopens billing to
  // undo a pending cancellation, so it drives `disabled`/`onClick` without
  // being treated as an upgrade anywhere else.
  const isActionableCta = isCheckoutCta || ctaKind === 'resume';
  let ctaClass = 'plan-cta';
  if (ctaKind === 'upgrade' || ctaKind === 'resume') ctaClass += ' cta-upgrade';
  else if (ctaKind === 'downgrade') ctaClass += ' cta-downgrade';
  else ctaClass += ' cta-current'; // current + unavailable share the muted look
  const ctaDisabled = !isActionableCta || pending;

  return (
    <div className={cardClass}>
      {/* Badge is funnel chrome — hidden in compact (checkout) mode. */}
      {badge && !compact ? (
        <Badge variant={badge.variant} className={`plan-card-badge ${BADGE_STATUS_CLASS}`}>
          <span className="inline-flex items-center justify-center gap-1">{badge.text}</span>
        </Badge>
      ) : null}

      <div className="plan-head">
        <div className="plan-name">{presentation.name}</div>
      </div>

      <div className="plan-price-block">
        <div className="plan-price-row">
          <span className="plan-price">{priceLabel ?? 'Custom'}</span>
          {showUnit ? (
            <span className="plan-unit">
              /mo{billingNote.unitSuffix ?? ''}
            </span>
          ) : null}
        </div>
        {billingNote.trial ? (
          // Trial leads (highest-conversion treatment A): emphasized sage-deep
          // headline in place of the muted note; the price above stays prominent.
          <div className="plan-trial-note">{billingNote.trial}</div>
        ) : (
          <div className="plan-billing-note">
            {billingNote.text}
            {billingNote.save ? <span className="save-inline">{billingNote.save}</span> : null}
          </div>
        )}
      </div>

      {/* Plan hero line — the tier's value headline. Sits ABOVE the Covered
          Commitment / fee line so the plan's positioning reads first, then the
          fee framing. Only renders for tiers that define a heroLine (Base, Plus,
          Pro); Starter/Enterprise are null → nothing renders. */}
      {presentation.heroLine ? (
        <div className="plan-hero-line">
          <HeroCheck />
          <span>{presentation.heroLine}</span>
        </div>
      ) : null}

      <div className="plan-fee-line">
        {feeMode === 'commitment' ? (
          // Covered Commitment framing: lead with the brand term, then state the
          // default ($0 to you) as a full stop, then the single condition under
          // which the fee applies. The % stays engine-driven (feeCovered) and is
          // intentionally NOT bold — only the $0 is emphasized.
          <>
            <span className="pfl-label">Covered Commitment</span>
            <span className="pfl-text">
              You pay <strong>$0</strong> in platform &amp; transaction fees. A{' '}
              {feeCovered ?? '—'} fee applies only if a donor chooses to cover it.
            </span>
          </>
        ) : (
          <>
            <span className="pfl-val">{feeUncovered ?? '—'}</span>
            <span className="pfl-text">platform fee on every gift + ~3% processing</span>
          </>
        )}
      </div>

      <div className="plan-features">
        {presentation.inheritFrom ? (
          <div className="pf-inherit">Everything in {presentation.inheritFrom}, plus</div>
        ) : null}

        {/* Collapsed top list — flat, no group labels. Shown ONLY below xl
            (CSS hides it at xl+, where the full grouped list shows instead). */}
        <ul className="pf-list pf-top">
          {topItems.map((it) => (
            <FeatureRow key={it.text} item={it} />
          ))}
        </ul>

        {/* See-all toggle — below xl only, and only when there's more to show.
            At xl+ it's hidden by CSS (full list always renders). Never shown in
            compact mode (the full grouped list is forced open above). */}
        {hasExtra && !compact ? (
          <div className="pf-seeall">
            <button
              type="button"
              aria-expanded={featuresExpanded}
              aria-controls={extraId}
              onClick={() => setFeaturesExpanded((v) => !v)}
            >
              {featuresExpanded ? 'Show less' : 'See all features'}
              <span className="pf-chev" aria-hidden>
                ↓
              </span>
            </button>
          </div>
        ) : null}

        {/* Full grouped list. Two renderings:
            - COMPACT (checkout/subscribe popup): each group is a collapsed
              disclosure headed by a section-header-bar (style-guide
              `SectionHeader` look, replicated in .cs-billing-scoped CSS so the
              billing reset doesn't flatten it). Collapsed by default keeps the
              popup short — resting height is one header row per group, and the
              buyer expands what they want (PE-1637). Native <details> gives the
              accessibility + keyboard toggle for free, no per-group React state.
            - FUNNEL (standalone billing grid): the always-open grouped list,
              unchanged. At xl+ always visible; below xl visible only when the
              card is expanded (the .features-open class). */}
        {compact ? (
          <div className="pf-accordions">
            <div className="pf-accordions-heading">Included features</div>
            {visibleGroups.map((g) => (
              // One collapsed dropdown per feature group (Variant 1). The count is
              // the group's item total; the body animates open/closed via a
              // grid-rows transition on .pf-acc-body (see billing-plans-page.css).
              <details className="pf-acc" key={g.label}>
                <summary className="pf-acc-bar">
                  <span className="pf-acc-label">{g.label}</span>
                  <span className="pf-acc-count">{g.items.length}</span>
                  <Chevron />
                </summary>
                <div className="pf-acc-body">
                  <ul className="pf-list pf-acc-list">
                    {g.items.map((it) => (
                      <FeatureRow key={it.text} item={it} />
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
        ) : (
          <div className="pf-full" id={extraId}>
            {visibleGroups.map((g) => (
              <div className="pf-group" key={g.label}>
                <div className="pf-group-label">{g.label}</div>
                <ul className="pf-list">
                  {g.items.map((it) => (
                    <FeatureRow key={it.text} item={it} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA is the funnel action — hidden in compact (checkout) mode, where the
          live Stripe form to the right IS the action and a second "Upgrade"
          button would be circular. */}
      {compact ? null : (
        <button
          type="button"
          className={ctaClass}
          disabled={ctaDisabled}
          title={ctaTitle}
          onClick={isActionableCta ? onUpgrade : undefined}
        >
          {pending ? 'Starting checkout…' : ctaLabel}
        </button>
      )}
    </div>
  );
}
